/**
 * Messages oldest → newest: sender, time, body, attachments, "via email" chip, edited and
 * deleted markers, system lines; messages from people the viewer blocked are collapsed.
 */
import type { MessageDto } from '@oex/contracts';
import { DateText, StatusChip } from '@oex/ui';
import { Mail, Paperclip } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { API_URL } from '../../../app/api';
import { useT } from '../../../app/i18n';

export function ViaEmailChip() {
  const t = useT();
  return <StatusChip status="via_email" tone="cyan" icon={Mail} label={t('messaging.message.viaEmail')} />;
}

export function MessageList({ messages, myUserId }: { messages: MessageDto[]; myUserId: string | null }) {
  const t = useT();
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length]);
  return (
    <ol className="flex flex-col gap-3" aria-label={t('messaging.thread.messages')}>
      {messages.map((m) => {
        if (m.kind === 'system') {
          return (
            <li key={m.id} className="text-center text-xs text-muted-foreground" data-message={m.id}>
              {m.body}
            </li>
          );
        }
        const mine = m.sender.userId !== null && m.sender.userId === myUserId;
        return (
          <li key={m.id} data-message={m.id} className={mine ? 'self-end text-right' : 'self-start'}>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">
                {mine ? t('messaging.message.you') : (m.sender.fullName ?? t('messaging.message.someone'))}
              </span>
              <DateText value={m.createdAt} withTime />
              {m.sender.viaEmail ? <ViaEmailChip /> : null}
              {m.editedAt && !m.deletedAt ? <span>{t('messaging.message.edited')}</span> : null}
            </div>
            <div className="mt-1 inline-block max-w-[36rem] rounded-[4px] border border-border bg-card px-3 py-2 text-left text-sm">
              {m.deletedAt ? (
                <em className="text-muted-foreground">{t('messaging.message.deleted')}</em>
              ) : m.hiddenByBlock ? (
                <em className="text-muted-foreground">{t('messaging.message.hiddenByBlock')}</em>
              ) : (
                <p className="whitespace-pre-line">{m.body}</p>
              )}
              {!m.hiddenByBlock && m.attachments.length ? (
                <ul className="mt-2 flex flex-col gap-1">
                  {m.attachments.map((a) => (
                    <li key={a.fileId}>
                      <a
                        className="inline-flex items-center gap-1 text-xs underline"
                        href={`${API_URL}${a.downloadPath}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Paperclip className="size-3" aria-hidden /> {a.originalName}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </li>
        );
      })}
      <div ref={end} />
    </ol>
  );
}
